/**
 * Browser notifications for green deals.
 *
 * The point is to catch a deal while the tab is buried behind something else,
 * so notifications carry the price and a click focuses the window.
 */

import { itemImage } from "../ui/links.ts";
import { money } from "../util/format.ts";

export type NotifyPermission = "default" | "granted" | "denied" | "unsupported";

export function notificationPermission(): NotifyPermission {
  if (typeof Notification === "undefined") return "unsupported";
  return Notification.permission;
}

export async function requestNotificationPermission(): Promise<NotifyPermission> {
  if (typeof Notification === "undefined") return "unsupported";
  if (Notification.permission !== "default") return Notification.permission;
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}

export interface DealNotification {
  itemId: number;
  itemName: string;
  price: number;
  marketValue: number;
  source: string;
  url: string;
  sellerName?: string | null;
}

export function notifyDeal(deal: DealNotification): void {
  if (typeof Notification === "undefined" || Notification.permission !== "granted") return;

  const discount =
    deal.marketValue > 0
      ? ` (${Math.round((1 - deal.price / deal.marketValue) * 100)}% under MV)`
      : "";
  const seller = deal.sellerName ? ` — ${deal.sellerName}` : "";

  try {
    const notification = new Notification(`${deal.itemName} — ${money(deal.price)}`, {
      body: `${deal.source}${seller}${discount}`,
      icon: itemImage(deal.itemId),
      // One notification per item; a newer deal replaces the older one rather
      // than stacking up a wall of them.
      tag: `sniper-${deal.itemId}`,
      renotify: true,
    } as NotificationOptions);

    notification.onclick = () => {
      window.focus();
      window.open(deal.url, "_blank", "noopener");
      notification.close();
    };
  } catch (err) {
    console.warn("[sniper] notification failed", err);
  }
}
